import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameDay, isSameMonth, isToday, setYear, getYear, parseISO,
} from 'date-fns';
import { de, cs, es, enUS } from 'date-fns/locale';
import { useTranslation } from '../i18n/LanguageContext';
import danzerLogo from '../assets/danzer-logo.svg';
import { BASE_AUTH_URL, BASE_URL } from '../components/constants';
import MfaEnrollment from '../components/MfaEnrollment';

const DANZER_COLOR = '#F28E04';

const LogoBadge = () => (
  <div className="flex items-center justify-center mb-6">
    <img src={danzerLogo} alt="Danzer" className="w-48 h-auto object-contain" />
  </div>
);

const DATE_LOCALES = { de, cs, es, en: enUS };
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[+]?[0-9\s\-()]{7,20}$/;

const SHOW_MICROSOFT_LOGIN = false;

const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const EyeIcon = ({ visible }) => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {visible ? (
      <>
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-11-7-11-7a18.6 18.6 0 0 1 4.22-5.06M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22" />
    )}
  </svg>
);

const BackArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5" />
    <path d="M12 19l-7-7 7-7" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

const ShieldIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9.5 12l1.8 1.8L14.5 10" />
  </svg>
);

const MailIcon = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M2 6l10 7 10-7" />
  </svg>
);

const Spinner = () => (
  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

// Standard field — matches the app's own form fields (Users.jsx inputCls): plain label above,
// 1px border with a JS-driven focus color swap instead of a floating label / border-2 ring.
const FloatingField = ({ label, trailing, inputRef, primaryColor, onFocus, onBlur, ...props }) => (
  <div>
    {label && <label className="block text-[14px] font-medium text-[#111827] mb-1.5">{label}</label>}
    <div className="relative">
      <input
        ref={inputRef}
        {...props}
        onFocus={(e) => { e.target.style.borderColor = primaryColor || '#6D28D9'; onFocus?.(e); }}
        onBlur={(e) => { e.target.style.borderColor = '#E5E7EB'; onBlur?.(e); }}
        style={{ borderColor: '#E5E7EB' }}
        className="w-full border rounded-lg px-4 py-3 text-[14px] text-gray-900 outline-none transition-colors disabled:bg-gray-50"
      />
      {trailing && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">{trailing}</div>
      )}
    </div>
  </div>
);

const useClickOutside = (ref, onOutside) => {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onOutside();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onOutside]);
};

const DatePicker = ({ label, value, onChange, primaryColor, t, language }) => {
  const dateLocale = DATE_LOCALES[language] || enUS;
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState('days');
  const [viewDate, setViewDate] = useState(value ? parseISO(value) : new Date());
  const containerRef = useRef(null);
  useClickOutside(containerRef, () => { setOpen(false); setViewMode('days'); });
  const selectedDate = value ? parseISO(value) : null;
  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(viewDate);
  const days = eachDayOfInterval({ start: startOfWeek(monthStart), end: endOfWeek(monthEnd) });
  const weekDayLabels = eachDayOfInterval({ start: startOfWeek(new Date()), end: endOfWeek(new Date()) })
    .map((d) => format(d, 'EEEEE', { locale: dateLocale }));
  const yearRangeStart = Math.floor(getYear(viewDate) / 12) * 12;
  const years = Array.from({ length: 12 }, (_, i) => yearRangeStart + i);
  const selectDay = (day) => { onChange(format(day, 'yyyy-MM-dd')); setOpen(false); setViewMode('days'); };
  return (
    <div className="relative" ref={containerRef}>
      <label className="absolute -top-2 left-3.5 px-1.5 bg-white text-[11px] text-gray-500 z-10">{label}</label>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full border border-gray-300 rounded-2xl px-4 py-[14px] text-[15px] text-left outline-none focus:border-2 focus:border-[var(--aispace-color)] transition-[border] flex items-center justify-between">
        <span className={selectedDate ? 'text-gray-900' : 'text-gray-400'}>
          {selectedDate ? format(selectedDate, 'dd.MM.yyyy') : 'TT.MM.JJJJ'}
        </span>
        <span className="text-gray-400"><CalendarIcon /></span>
      </button>
      {open && (
        <div className="absolute z-30 mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl p-4 w-[300px]">
          {viewMode === 'days' ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <button type="button" onClick={() => setViewDate((d) => subMonths(d, 1))} className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100"><ChevronLeftIcon /></button>
                <button type="button" onClick={() => setViewMode('years')} className="text-[14px] font-semibold text-gray-900 hover:opacity-70">
                  {format(viewDate, 'MMMM yyyy', { locale: dateLocale })}
                </button>
                <button type="button" onClick={() => setViewDate((d) => addMonths(d, 1))} className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100"><ChevronRightIcon /></button>
              </div>
              <div className="grid grid-cols-7 mb-1">
                {weekDayLabels.map((d, i) => (<div key={i} className="text-center text-[11px] text-gray-400 font-medium py-1">{d}</div>))}
              </div>
              <div className="grid grid-cols-7 gap-y-1">
                {days.map((day) => {
                  const inMonth = isSameMonth(day, viewDate);
                  const selected = selectedDate && isSameDay(day, selectedDate);
                  return (
                    <button key={day.toISOString()} type="button" onClick={() => selectDay(day)}
                      className={`h-9 w-9 rounded-full text-[13px] flex items-center justify-center mx-auto transition
                        ${!inMonth ? 'text-gray-300' : 'text-gray-700'}
                        ${selected ? 'text-white font-medium' : 'hover:bg-gray-100'}
                        ${!selected && isToday(day) ? 'ring-1 ring-gray-300' : ''}`}
                      style={selected ? { backgroundColor: primaryColor } : undefined}>
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                <button type="button" onClick={() => { onChange(''); setOpen(false); }} className="text-[13px] font-medium" style={{ color: primaryColor }}>{t('auth.clear')}</button>
                <button type="button" onClick={() => selectDay(new Date())} className="text-[13px] font-medium" style={{ color: primaryColor }}>{t('auth.today')}</button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <button type="button" onClick={() => setViewDate((d) => setYear(d, getYear(d) - 12))} className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100"><ChevronLeftIcon /></button>
                <span className="text-[14px] font-semibold text-gray-900">{years[0]} - {years[11]}</span>
                <button type="button" onClick={() => setViewDate((d) => setYear(d, getYear(d) + 12))} className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100"><ChevronRightIcon /></button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {years.map((year) => (
                  <button key={year} type="button" onClick={() => { setViewDate((d) => setYear(d, year)); setViewMode('days'); }}
                    className={`py-2 rounded-lg text-[13px] hover:bg-gray-100 ${year === getYear(viewDate) ? 'font-semibold' : 'text-gray-700'}`}
                    style={year === getYear(viewDate) ? { color: primaryColor } : undefined}>
                    {year}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const Login = () => {
  const { t, language } = useTranslation();
  const primaryColor = DANZER_COLOR;

  // 'password' | 'choose' | 'otp' | 'profile' | 'set-password' | 'mfa-verify' | 'mfa-setup'
  // | 'reset' | 'reset-sent' | 'change-password'
  const [step, setStep] = useState('password');
  const [email, setEmail] = useState('');
  const [hasPassword, setHasPassword] = useState(false);
  const [signupToken, setSignupToken] = useState('');
  const [setPasswordToken, setSetPasswordToken] = useState('');
  const [changePasswordToken, setChangePasswordToken] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaSetupToken, setMfaSetupToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [fullname, setFullname] = useState('');
  const [dob, setDob] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // SSO callback: pick up mfa_token, mfa_setup_token, or a deactivated-account error from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('sso_error');
    if (ssoError) {
      window.history.replaceState({}, '', window.location.pathname);
      setError(ssoError);
      return;
    }
    const ssoEmail = params.get('email');
    const incomingMfaToken = params.get('mfa_token');
    const incomingMfaSetupToken = params.get('mfa_setup_token');
    if (!ssoEmail || (!incomingMfaToken && !incomingMfaSetupToken)) return;
    window.history.replaceState({}, '', window.location.pathname);
    setEmail(ssoEmail);
    if (incomingMfaToken) {
      setMfaToken(incomingMfaToken);
      goToStep('mfa-verify');
    } else {
      setMfaSetupToken(incomingMfaSetupToken);
      goToStep('mfa-setup');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToStep = (newStep) => { setError(''); setInfo(''); setStep(newStep); };

  const goBack = (newStep) => {
    setEmail('');
    setHasPassword(false);
    setSignupToken('');
    setSetPasswordToken('');
    setMfaToken('');
    setMfaSetupToken('');
    setMfaCode('');
    setUseRecoveryCode(false);
    setCode('');
    setPassword('');
    setNewPassword('');
    setShowPassword(false);
    setFullname('');
    setDob('');
    setMobileNumber('');
    goToStep(newStep);
  };

  const completeLogin = (data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('email', data.email);
    localStorage.setItem('name', data.name);
    localStorage.setItem('id', data.id);
    localStorage.setItem('language', 'de');
    window.location.replace('/');
  };

  const handleError = (err) => setError(err?.response?.data?.detail || t('auth.genericError'));

  const handleLoginResult = (data) => {
    if (data.needs_mfa) {
      setMfaToken(data.mfa_token);
      goToStep('mfa-verify');
    } else if (data.needs_mfa_setup) {
      setMfaSetupToken(data.mfa_setup_token);
      goToStep('mfa-setup');
    } else {
      completeLogin(data);
    }
  };

  const continueWithMicrosoft = () => { window.location.href = `${BASE_URL}/microsoft/login`; };

  // OTP-based sign-up / passwordless login
  const requestOtp = async () => {
    setError('');
    if (!EMAIL_REGEX.test(email.trim())) { setError(t('auth.invalidEmail')); return; }
    setOtpLoading(true);
    try {
      const res = await axios.post(`${BASE_AUTH_URL}/request-otp`, { email: email.trim(), language });
      setHasPassword(res.data.has_password);
      setInfo(t('auth.otpSent'));
      setStep('otp');
    } catch (err) {
      handleError(err);
    } finally {
      setOtpLoading(false);
    }
  };

  const submitSignupEmail = async (e) => { e.preventDefault(); await requestOtp(); };

  const resendOtp = async () => {
    setError('');
    setLoading(true);
    try {
      await axios.post(`${BASE_AUTH_URL}/request-otp`, { email: email.trim(), language });
      setInfo(t('auth.otpResent'));
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${BASE_AUTH_URL}/verify-otp`, { email: email.trim(), code });
      if (res.data.needs_profile) {
        setSignupToken(res.data.signup_token);
        goToStep('profile');
      } else if (res.data.needs_password_setup) {
        setSetPasswordToken(res.data.set_password_token);
        setFullname(res.data.fullname || '');
        goToStep('set-password');
      } else {
        handleLoginResult(res.data);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const submitProfile = async (e) => {
    e.preventDefault();
    setError('');
    if (!fullname.trim() || !dob || !mobileNumber.trim()) { setError(t('auth.fieldRequired')); return; }
    if (!PHONE_REGEX.test(mobileNumber.trim())) { setError(t('auth.invalidPhone')); return; }
    if (password.length < 8) { setError(t('auth.passwordTooShort')); return; }
    setLoading(true);
    try {
      const res = await axios.post(
        `${BASE_AUTH_URL}/complete-signup`,
        { email: email.trim(), fullname, date_of_birth: dob, mobile_number: mobileNumber, password },
        { headers: { Authorization: `Bearer ${signupToken}` } }
      );
      handleLoginResult(res.data);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const submitSetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError(t('auth.passwordTooShort')); return; }
    setLoading(true);
    try {
      const res = await axios.post(
        `${BASE_AUTH_URL}/set-password`,
        { email: email.trim(), password },
        { headers: { Authorization: `Bearer ${setPasswordToken}` } }
      );
      handleLoginResult(res.data);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!EMAIL_REGEX.test(email.trim())) { setError(t('auth.invalidEmail')); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${BASE_AUTH_URL}/login`, { email: email.trim(), password });
      if (res.data.needs_password_change) {
        setChangePasswordToken(res.data.change_password_token);
        setNewPassword('');
        goToStep('change-password');
        return;
      }
      handleLoginResult(res.data);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const submitChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) { setError(t('auth.passwordTooShort')); return; }
    setLoading(true);
    try {
      const res = await axios.post(
        `${BASE_AUTH_URL}/change-password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${changePasswordToken}` } },
      );
      handleLoginResult(res.data);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const submitMfaVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(
        `${BASE_AUTH_URL}/mfa/verify`,
        { email: email.trim(), code: mfaCode },
        { headers: { Authorization: `Bearer ${mfaToken}` } }
      );
      completeLogin(res.data);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const requestReset = async (e) => {
    e.preventDefault();
    setError('');
    if (!EMAIL_REGEX.test(email.trim())) { setError(t('auth.invalidEmail')); return; }
    setLoading(true);
    try {
      await axios.post(`${BASE_AUTH_URL}/reset-password/request`, { email: email.trim(), language });
      goToStep('reset-sent');
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  // Back targets — 'password' is the entry point so it has no back button
  const BACK_TARGET = {
    otp: 'password',
    profile: 'password',
    'set-password': 'password',
    'mfa-verify': 'password',
    'mfa-setup': 'password',
    reset: 'password',
    'reset-sent': 'reset',
    // change-password has no back — user must set password before accessing the app
  };

  const primaryButtonClass = 'w-full py-3.5 rounded-xl text-white text-[15px] font-semibold transition hover:brightness-90 active:brightness-75 disabled:opacity-60 disabled:hover:brightness-100 flex items-center justify-center gap-2';
  const outlineButtonClass = 'w-full py-3.5 rounded-xl border border-gray-300 text-[15px] font-medium text-gray-800 flex items-center justify-center gap-2.5 hover:bg-gray-50 transition disabled:opacity-60';
  const linkButtonClass = 'text-[14px] font-medium transition hover:opacity-75';

  const OtpDivider = () => (
    <div className="flex items-center gap-3 my-5">
      <div className="flex-1 h-px bg-gray-200" />
      <span className="text-[11px] font-medium tracking-wide text-gray-400">{t('auth.or')}</span>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  );

  const PageFooter = () => (
    <div className="flex items-center justify-center gap-6 mt-6 text-[13px] text-gray-500">
      <a href="#" className="hover:text-gray-700 transition">{t('auth.footerPrivacyPolicy')}</a>
      <a href="#" className="hover:text-gray-700 transition">{t('auth.footerTermsOfService')}</a>
      <a href="#" className="hover:text-gray-700 transition">{t('auth.footerSupport')}</a>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F2F2F2] p-6" style={{ '--aispace-color': DANZER_COLOR }}>
      <div className="relative bg-white rounded-[24px] shadow-[0_4px_48px_rgba(0,0,0,0.10)] w-full max-w-[440px] px-10 py-10">

        {/* Back button — shown on all steps except the entry point; positioned out of flow so it never pushes the content below down */}
        {BACK_TARGET[step] && (
          <button type="button" onClick={() => goBack(BACK_TARGET[step])}
            className="absolute top-6 left-6 p-1 text-gray-500 hover:text-gray-800 transition" aria-label={t('common.back')}>
            <BackArrowIcon />
          </button>
        )}

        {/* Error / info banners */}
        {error && (
          <div className="mb-5 text-[13px] text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">{error}</div>
        )}
        {!error && info && (
          <div className="mb-5 text-[13px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">{info}</div>
        )}

        {/* ── MAIN LOGIN SCREEN ── */}
        {step === 'password' && (
          <form onSubmit={submitPassword}>
            <LogoBadge />

            <h1 className="text-[28px] font-bold text-center mb-1.5 tracking-tight text-gray-900">
              {t('auth.welcomeBack')}
            </h1>
            <p className="text-gray-400 text-center text-[14px] mb-7 leading-relaxed">
              {t('auth.loginPageSubtitle')}
            </p>

            <div className="space-y-5">
              <FloatingField primaryColor={primaryColor}
                label={t('auth.emailLabel')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus={!email}
              />
              <FloatingField primaryColor={primaryColor}
                label={t('auth.passwordLabel')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus={!!email}
                trailing={(
                  <button type="button" onClick={() => setShowPassword((v) => !v)} className="pointer-events-auto">
                    <EyeIcon visible={showPassword} />
                  </button>
                )}
              />
            </div>

            {/* Remember me + Forgot password */}
            <div className="flex items-center justify-between mt-3.5 mb-5">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                  style={{ accentColor: primaryColor }}
                />
                <span className="text-[13px] text-gray-600">{t('auth.rememberMe')}</span>
              </label>
              <button type="button" onClick={() => goToStep('reset')} className={linkButtonClass} style={{ color: primaryColor }}>
                {t('auth.forgotPassword')}
              </button>
            </div>

            <button type="submit" disabled={loading} className={primaryButtonClass} style={{ backgroundColor: primaryColor }}>
              {loading ? <Spinner /> : t('auth.loginButton')}
            </button>

            {SHOW_MICROSOFT_LOGIN && (
              <>
                {/* OR CONTINUE WITH */}
                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-[11px] font-medium tracking-widest text-gray-400">{t('auth.orContinueWith')}</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>

                <button type="button" onClick={continueWithMicrosoft} className={outlineButtonClass}>
                  <MicrosoftIcon />
                  {t('auth.continueWithMicrosoft')}
                </button>
              </>
            )}

          </form>
        )}

        {/* ── OTP VERIFICATION ── */}
        {step === 'otp' && (
          <form onSubmit={submitOtp}>
            <h1 className="text-[26px] font-semibold text-center mb-2 tracking-tight text-gray-900">
              {t('auth.checkInboxTitle')}
            </h1>
            <p className="text-gray-500 text-center text-[14px] mb-8 leading-relaxed">
              {t('auth.checkInboxSubtitle', { email })}
            </p>
            <div className="mb-5">
              <FloatingField primaryColor={primaryColor} label={t('auth.codePlaceholder')} type="text" value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
            </div>
            <button type="submit" disabled={loading} className={primaryButtonClass} style={{ backgroundColor: primaryColor }}>
              {loading ? <Spinner /> : t('common.continue')}
            </button>
            <div className="text-center mt-4 mb-1">
              <button type="button" onClick={resendOtp} className={linkButtonClass} style={{ color: primaryColor }}>
                {t('auth.resendEmail')}
              </button>
            </div>
            {hasPassword && (
              <>
                <OtpDivider />
                <button type="button" onClick={() => goToStep('password')} className={outlineButtonClass}>
                  {t('auth.continueWithPassword')}
                </button>
              </>
            )}
          </form>
        )}

        {/* ── COMPLETE PROFILE ── */}
        {step === 'profile' && (
          <form onSubmit={submitProfile}>
            <h1 className="text-[26px] font-semibold text-center mb-2 tracking-tight text-gray-900">
              {t('auth.completeProfileTitle')}
            </h1>
            <p className="text-gray-500 text-center text-[14px] mb-8 leading-relaxed">
              {t('auth.completeProfileSubtitle')}
            </p>
            <div className="space-y-4">
              <FloatingField primaryColor={primaryColor} label={t('auth.fullnameLabel')} type="text" value={fullname} onChange={(e) => setFullname(e.target.value)} autoFocus />
              <DatePicker label={t('auth.dobLabel')} value={dob} onChange={setDob} primaryColor={primaryColor} t={t} language={language} />
              <FloatingField primaryColor={primaryColor} label={t('auth.mobileLabel')} type="tel" value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)} />
              <FloatingField primaryColor={primaryColor} label={t('auth.passwordLabel')} type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                trailing={(
                  <button type="button" onClick={() => setShowPassword((v) => !v)} className="pointer-events-auto"><EyeIcon visible={showPassword} /></button>
                )}
              />
            </div>
            <button type="submit" disabled={loading} className={primaryButtonClass + ' mt-7'} style={{ backgroundColor: primaryColor }}>
              {loading ? <Spinner /> : t('common.continue')}
            </button>
          </form>
        )}

        {/* ── SET PASSWORD ── */}
        {step === 'set-password' && (
          <form onSubmit={submitSetPassword}>
            <h1 className="text-[26px] font-semibold text-center mb-2 tracking-tight text-gray-900">
              {t('auth.setPasswordTitle')}
            </h1>
            <p className="text-gray-500 text-center text-[14px] mb-8 leading-relaxed">
              {t('auth.setPasswordSubtitle', { fullname })}
            </p>
            <FloatingField primaryColor={primaryColor} label={t('auth.passwordLabel')} type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoFocus
              trailing={(
                <button type="button" onClick={() => setShowPassword((v) => !v)} className="pointer-events-auto"><EyeIcon visible={showPassword} /></button>
              )}
            />
            <button type="submit" disabled={loading} className={primaryButtonClass + ' mt-4'} style={{ backgroundColor: primaryColor }}>
              {loading ? <Spinner /> : t('common.continue')}
            </button>
          </form>
        )}

        {/* ── MFA VERIFY ── */}
        {step === 'mfa-verify' && (
          <form onSubmit={submitMfaVerify}>
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}1A` }}>
                <ShieldIcon color={primaryColor} />
              </div>
            </div>
            <h1 className="text-[26px] font-semibold text-center mb-2 tracking-tight text-gray-900">{t('auth.mfaVerifyTitle')}</h1>
            <p className="text-gray-500 text-center text-[14px] mb-8 leading-relaxed">
              {useRecoveryCode ? t('auth.mfaRecoveryVerifySubtitle') : t('auth.mfaVerifySubtitle')}
            </p>
            <input type="text" inputMode={useRecoveryCode ? 'text' : 'numeric'} value={mfaCode} onChange={(e) => setMfaCode(e.target.value)}
              placeholder={useRecoveryCode ? 'xxxxx-xxxxx' : '••••••'} autoFocus autoComplete="one-time-code"
              className={`w-full border border-gray-300 rounded-2xl px-4 py-[14px] text-center font-semibold outline-none focus:border-2 focus:border-[var(--aispace-color)] transition-[border] ${useRecoveryCode ? 'text-[18px] tracking-[3px] font-mono' : 'text-[22px] tracking-[8px]'}`}
            />
            <button type="submit" disabled={loading} className={primaryButtonClass + ' mt-5'} style={{ backgroundColor: primaryColor }}>
              {loading ? <Spinner /> : t('common.continue')}
            </button>
            <div className="text-center mt-4">
              <button type="button" onClick={() => { setUseRecoveryCode((v) => !v); setMfaCode(''); setError(''); }}
                className={linkButtonClass} style={{ color: primaryColor }}>
                {useRecoveryCode ? t('auth.mfaUseAuthenticatorCode') : t('auth.mfaUseRecoveryCode')}
              </button>
            </div>
          </form>
        )}

        {/* ── MFA SETUP ── */}
        {step === 'mfa-setup' && (
          <div>
            <LogoBadge />
            <h1 className="text-[24px] font-semibold text-center mb-2 tracking-tight text-gray-900">{t('auth.mfaSetupTitle')}</h1>
            <MfaEnrollment authorization={`Bearer ${mfaSetupToken}`} primaryColor={primaryColor} onComplete={completeLogin} />
          </div>
        )}

        {/* ── FORGOT PASSWORD ── */}
        {step === 'reset' && (
          <form onSubmit={requestReset}>
            <LogoBadge />
            <h1 className="text-[26px] font-semibold text-center mb-2 tracking-tight text-gray-900">{t('auth.resetPasswordTitle')}</h1>
            <p className="text-gray-500 text-center text-[14px] mb-8 leading-relaxed">{t('auth.resetPasswordEmailPrompt')}</p>
            <FloatingField primaryColor={primaryColor} label={t('auth.emailLabel')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
            <button type="submit" disabled={loading} className={primaryButtonClass + ' mt-4'} style={{ backgroundColor: primaryColor }}>
              {loading ? <Spinner /> : t('auth.sendResetLinkButton')}
            </button>
          </form>
        )}

        {/* ── RESET LINK SENT ── */}
        {step === 'reset-sent' && (
          <div>
            <div className="flex justify-center mb-5">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}1A` }}>
                <MailIcon color={primaryColor} />
              </div>
            </div>
            <h1 className="text-[26px] font-semibold text-center mb-2 tracking-tight text-gray-900">{t('auth.resetLinkSentTitle')}</h1>
            <p className="text-gray-500 text-center text-[14px] mb-8 leading-relaxed">{t('auth.resetLinkSentSubtitle', { email })}</p>
            <button type="button" onClick={() => goBack('password')} className={primaryButtonClass} style={{ backgroundColor: primaryColor }}>
              {t('auth.backToLogin')}
            </button>
          </div>
        )}

        {/* ── FIRST-LOGIN CHANGE PASSWORD (admin-created users) ── */}
        {step === 'change-password' && (
          <form onSubmit={submitChangePassword}>
            <div className="flex justify-center mb-5">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}1A` }}>
                <ShieldIcon color={primaryColor} />
              </div>
            </div>
            <h1 className="text-[26px] font-semibold text-center mb-2 tracking-tight text-gray-900">
              {t('users.changePasswordTitle')}
            </h1>
            <p className="text-gray-500 text-center text-[14px] mb-8 leading-relaxed">
              {t('users.changePasswordSubtitle')}
            </p>
            <FloatingField primaryColor={primaryColor}
              label={t('auth.newPasswordPlaceholder')}
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoFocus
              trailing={(
                <button type="button" onClick={() => setShowPassword((v) => !v)} className="pointer-events-auto">
                  <EyeIcon visible={showPassword} />
                </button>
              )}
            />
            <button type="submit" disabled={loading} className={primaryButtonClass + ' mt-4'} style={{ backgroundColor: primaryColor }}>
              {loading ? <Spinner /> : t('users.changePasswordButton')}
            </button>
          </form>
        )}

      </div>
      {step === 'reset' && <PageFooter />}
    </div>
  );
};

export default Login;
